import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Icon, Input } from "@zorni/components-lib";

import { useAdmin } from "../../hooks/useAdmin";
import { useOrganismeData } from "./hooks/useOrganismeData";
import useFormValidation from "./hooks/useAddOrganisme.hook";
import { useUpload } from "../../../common/hooks/useUpload";
import { UploadError } from "../../../common/hooks/uploadError";
import { useOperation } from "../../hooks/useOperation";
import Preloader from "../../../common/preLoader/PreLoader";
import AddressForm from "../../common/addressForm/AddressForm";
import { findCountryCode } from "../../../common/countries/countries";
import OrganismeLogoUpload from "./OrganismeLogoUpload";
import { UPLOAD_RESOURCES, UPLOAD_TYPES } from "../../common/model/enum";

import styles from "./Organisme.module.css";

const initialAddress = {
  number: "",
  route: "",
  complement: "",
  stage: "",
  zipPostal: "",
  city: "",
};

const AddOrganisme = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { organization, addOrganization } = useAdmin();
  const { errors, validate, clearErrors } = useFormValidation();
  const { showToast } = useOperation();

  const { organisme, loading, addOrganisme, updateOrganisme } = useOrganismeData(
    organization?.entityId
  );
  const { uploadFile, updateFile, getUploadIdFromUrl } = useUpload();

  const [formData, setFormData] = useState({
    name: "",
    picture: "",
    address: initialAddress,
  });
  // Code ISO du pays (ex. "FR") : c'est la valeur du champ Pays ET ce que
  // reçoit la mutation (`address.country` est un code côté API). Vide tant
  // qu'aucun pays n'est choisi dans la liste, ce qui verrouille ville et code
  // postal (verrouillage et invalidation gérés par AddressForm).
  const [countryCode, setCountryCode] = useState("");
  const [pickedFile, setPickedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (organisme) {
      // Ancien enregistrement : le pays peut être un nom libre plutôt qu'un code.
      const storedCountryCode = findCountryCode(organisme.address?.country, i18n.language);
      setFormData({
        name: organisme.name || "",
        picture: organisme.picture || "",
        address: {
          number: organisme.address?.number || "",
          route: organisme.address?.route || "",
          complement: organisme.address?.complement || "",
          stage: organisme.address?.stage || "",
          zipPostal: organisme.address?.zipPostal || "",
          city: organisme.address?.city || "",
        },
      });
      setCountryCode(storedCountryCode);
    }
    // i18n.language volontairement absent : il ne sert qu'à retrouver un code
    // depuis un ancien nom de pays, et le relancer réinitialiserait le formulaire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisme]);

  // Nettoie l'URL d'aperçu locale (createObjectURL) quand elle change / au démontage.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleInputChange = (field, value, isAddress = false) => {
    if (isAddress) {
      setFormData((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleFileSelected = (file) => {
    setPickedFile(file);
    setPreviewUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return URL.createObjectURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!validate(formData)) return;

    setIsSaving(true);
    try {
      let finalPicture = formData.picture;

      if (pickedFile) {
        const uploadId = getUploadIdFromUrl(organisme?.picture);
        const uploadRes = uploadId
          ? await updateFile(uploadId, pickedFile, UPLOAD_RESOURCES.PARTNER, UPLOAD_TYPES.PHOTO)
          : await uploadFile(pickedFile, UPLOAD_RESOURCES.PARTNER, UPLOAD_TYPES.PHOTO);

        finalPicture = uploadRes?.fileLink || finalPicture;
      }

      // Adresse entièrement vide (organisme sans adresse) : on envoie
      // address: null plutôt qu'un objet avec des chaînes vides.
      const hasAddress = [...Object.values(formData.address), countryCode].some((value) =>
        (value || "").trim()
      );

      const payload = {
        name: formData.name,
        picture: finalPicture,
        // address.country est un countryCode ISO côté API (ex. "FR"), pas le
        // libellé affiché ("France").
        address: hasAddress ? { ...formData.address, country: countryCode } : null,
      };

      if (organization) {
        await updateOrganisme(organisme.id, payload);
      } else {
        const newOrg = await addOrganisme(payload);
        if (newOrg) addOrganization(newOrg.id);
      }

      clearErrors();
      showToast(
        t("organism.saveSuccess", "Organisme enregistré."),
        "success"
      );
      navigate("/admin/organisme");
    } catch (err) {
      console.error(err);
      // Échec d'upload : on n'a rien enregistré (ni upload, ni mutation GraphQL).
      showToast(
        err instanceof UploadError
          ? t("organism.logoUploadError", {
              defaultValue:
                "Impossible d'envoyer le logo (erreur {{status}}). Rien n'a été enregistré. Référence : {{reference}}",
              status: err.status ?? "N/A",
              reference: err.correlationId ?? "N/A",
            })
          : t("organism.saveError", "Impossible d'enregistrer l'organisme pour le moment."),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.centerState}>
        <Preloader />
      </div>
    );
  }

  // Aperçu local utilisé uniquement dans le panneau "Identité" (widget d'upload).
  const displayPicture = previewUrl || formData.picture;

  // L'en-tête ne reflète que les données enregistrées (organisme) : le nom et le
  // logo saisis dans le formulaire ne doivent pas s'y répercuter avant un save réussi.
  const headName = organisme?.name || t("organism.newOrganism", "Nouvel organisme");
  const headPicture = organisme?.picture || "";

  return (
    <div className={styles.root}>
      <div className={styles.pageHead}>
        <h1>{t("organism.pageTitle", "Mon organisme")}</h1>
        <p>
          {t(
            "organism.editSubtitle",
            "Renseignez les informations légales de votre organisme."
          )}
        </p>
      </div>

      <section className={styles.orgCard}>
        <div className={styles.orgHead}>
          <span className={styles.orgLogoRing}>
            {headPicture ? (
              <img className={styles.orgHeadLogo} src={headPicture} alt="" />
            ) : (
              <span className={styles.orgHeadLogoFallback}>
                {headName.charAt(0).toUpperCase()}
              </span>
            )}
          </span>
          <div className={styles.orgIdText}>
            <strong>{headName}</strong>
          </div>
        </div>

        <div className={styles.orgBody}>
          <div className={styles.kycGrid}>
            <div className={styles.panel}>
              <p className={styles.panelTitle}>
                <span className={styles.panelIcon}>
                  <Icon name="iconBuildings" size="sm" />
                </span>
                {t("organism.sections.identity", "Identité")}
              </p>
              <div className={styles.pgrid}>
                <div className={styles.span2}>
                  <Input
                    floatingLabel
                    inputSize="medium"
                    label={t("commons.labels.name")}
                    name="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    errors={errors?.name}
                  />
                </div>
                <div className={styles.span2}>
                  <OrganismeLogoUpload
                    previewUrl={displayPicture}
                    onFileSelected={handleFileSelected}
                  />
                </div>
              </div>
            </div>

            <div className={styles.panel}>
              <p className={styles.panelTitle}>
                <span className={styles.panelIcon}>
                  <Icon name="iconLocation" size="sm" />
                </span>
                {t("commons.adress.label", "Adresse")}
              </p>
              <AddressForm
                value={{ address: formData.address, countryCode }}
                onChange={({ address, countryCode: nextCode }) => {
                  setFormData((prev) => ({ ...prev, address }));
                  setCountryCode(nextCode);
                }}
                errors={errors}
              />
            </div>
          </div>
        </div>

        <div className={styles.orgFooter}>
          <span className={styles.footerHint}>
            <Icon name="iconCompliance" size="sm" />
            {t("organism.rgpdHint", "Données chiffrées & conformes RGPD")}
          </span>
          <div className={styles.footerActions}>
            <Button
              variant="secondary"
              label={t("commons.buttons.cancel")}
              onClick={() => navigate("/admin/organisme")}
              disabled={isSaving}
            />
            <Button
              variant="primary"
              iconName="iconCheck"
              iconPosition="left"
              label={
                isSaving
                  ? t("commons.allActions.saving", "Enregistrement…")
                  : t("commons.allActions.save")
              }
              onClick={handleSubmit}
              disabled={isSaving}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddOrganisme;
